import { Autocomplete, Box, TextField, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { ReactComponent as SuccessIcon } from "../../../assets/Images/SuccessProjectIcon.svg";
import { commonStyles } from "../../../assets/MUIStyles/commonStyles";
import CommonModal from "../CommonComponents/Modal/CommonModal";
import CommonPopup from "../CommonComponents/PopUpCards/CommonPopup";
import ErrorPopUp from "../CommonComponents/PopUpCards/ErrorPopUp";
import { excelLeadDataAction } from "../Projects/Store/projects.action";
import { getWorkflowsAction } from "./Store/workflows.action";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CloseIcon from "@mui/icons-material/Close";
import { leadDataStyles } from "../../../assets/MUIStyles/leadDataStyle";

const WorkflowModal = ({ workflowModal, setWorkflowModal, fetchLeadsData }) => {
  const dispatch = useDispatch();
  const [workflows, setWorkflows] = useState([]);
  const [selectedWorkflow, setSelectedWorkflow] = useState(null) as any;
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [successPopup, setSuccessPopup] = useState(false);
  const [errorObj, setErrorObj] = useState({
    open: false,
    errorMsg: "",
  });

  useEffect(() => {
    const init = async () => {
      const res: any = await dispatch(getWorkflowsAction());
      const workflowData = res
        ?.map((item) => {
          if (item?.type === "workflow") {
            return item;
          }
        })
        ?.filter(Boolean);

      const workflowLists = workflowData?.map((wf) => ({
        id: wf._id,
        name: wf?.workflows?.name,
        workflow: { ...wf?.workflows, id: wf?._id },
      }));

      if (workflowLists?.length > 0) {
        setWorkflows(workflowLists);
      }
    };

    init();
  }, []);

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const fileName = file.name.toLowerCase();
    const isValid =
      fileName.endsWith(".csv") ||
      fileName.endsWith(".xlsx") ||
      fileName.endsWith(".xls");

    if (isValid) {
      setFile(file);
    } else {
      setErrorObj({
        open: true,
        errorMsg: "Please upload a .csv or .xlsx/.xls file.",
      });
      setFile(null);
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const droppedFile = event.dataTransfer.files?.[0];
    if (!droppedFile) return;

    const fileName = droppedFile.name.toLowerCase();
    const isValid =
      fileName.endsWith(".csv") ||
      fileName.endsWith(".xlsx") ||
      fileName.endsWith(".xls");

    if (isValid) {
      setFile(droppedFile);
    } else {
      setErrorObj({
        open: true,
        errorMsg: "Please upload a .csv or .xlsx/.xls file.",
      });
      setFile(null);
    }
  };

  const handleAddWorkflow = async () => {
    setLoading(true);
    if (!file) {
      setErrorObj({
        open: true,
        errorMsg: "File is required.",
      });
      setLoading(true);
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    formData.append("workflowId", selectedWorkflow?.id);

    const res: any = await dispatch(excelLeadDataAction(formData));

    if (res?.status !== 200) {
      setErrorObj({
        open: true,
        errorMsg: res?.errMsg,
      });
      setLoading(false);
    } else {
      setSuccessPopup(true);
      setTimeout(() => {
        setWorkflowModal(!workflowModal);
      }, 2000);
      setFile(null);
      setSelectedWorkflow(null);
    }
    fetchLeadsData(false);
    setLoading(false);
  };

  return (
    <CommonModal
      body={
        <Box display="flex" flexDirection="column" gap={2}>
          <Box>
            <Typography className="font-14-grey mb-1">Upload</Typography>
            <Box
              onDragOver={(e) => e.preventDefault()}
              onDrop={handleDrop}
              sx={leadDataStyles.excelUploadContainer}
              onClick={() => document.getElementById("fileInput")?.click()}
            >
              {!file ? (
                <Box>
                  <CloudUploadIcon sx={leadDataStyles.uploadIcon} />
                  <Typography fontSize={14} fontWeight={600} color="#FF6A00">
                    Drag & Drop your file here
                  </Typography>
                  <Typography fontSize={12} color="text.secondary">
                    or{" "}
                    <span style={{ color: "#FF6A00", fontWeight: 500 }}>
                      Click to Browse
                    </span>
                  </Typography>
                  <Typography fontSize={11} color="text.disabled" mt={1}>
                    Supports: .csv, .xlsx, .xls
                  </Typography>
                </Box>
              ) : (
                <Box sx={leadDataStyles.fileNameContainer}>
                  <Typography
                    fontSize={13}
                    fontWeight={500}
                    sx={leadDataStyles.fileNameText}
                  >
                    {file.name}
                  </Typography>
                  <CloseIcon
                    onClick={(e) => {
                      e.stopPropagation();
                      setFile(null);
                    }}
                    sx={leadDataStyles.closeIcon}
                  />
                </Box>
              )}
              <input
                id="fileInput"
                type="file"
                accept=".csv,.xlsx,.xls"
                hidden
                onChange={handleFileUpload}
              />
            </Box>
          </Box>
          <Box>
            <Typography className="font-14-grey mb-1">
              Select Workflow
            </Typography>
            <Autocomplete
              options={workflows}
              getOptionLabel={(option) => {
                return option?.name || "No name";
              }}
              value={selectedWorkflow || null}
              onChange={(event, newValue) => {
                setSelectedWorkflow(newValue);
              }}
              renderOption={(props, option) => (
                <li
                  style={{
                    fontSize: "11px",
                    padding: "8px 18px",
                  }}
                  {...props}
                  key={`${option.id}`}
                >
                  {option?.name || "No Workflow Name"}
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select Workflow"
                  sx={{
                    ...commonStyles.inputField,
                    "& .MuiAutocomplete-input": {
                      padding: "12px 15px !important",
                    },
                  }}
                />
              )}
              isOptionEqualToValue={(option, value) =>
                `${option.id}_${option.name}` === `${value.id}_${value.name}`
              }
              disableClearable
            />
          </Box>
          {errorObj.open && (
            <ErrorPopUp
              show={errorObj.open}
              toggle={() =>
                setErrorObj((prev) => ({
                  ...prev,
                  open: false,
                  errorMsg: "",
                }))
              }
              heading={"Error"}
              message={errorObj?.errorMsg}
              spin={false}
            />
          )}
          {successPopup && (
            <CommonPopup
              message={"Lead Data Added Successfully"}
              show={successPopup}
              toggle={() => setSuccessPopup((prev) => !prev)}
              Icon={SuccessIcon}
              heading={"Lead Data Added"}
            />
          )}
        </Box>
      }
      handleSave={handleAddWorkflow}
      heading={`Add Contacts`}
      show={workflowModal}
      size={600}
      toggle={() => {
        setWorkflowModal(!workflowModal);
        setSelectedWorkflow(null);
        setFile(null);
      }}
      saveButtonText="Add Contacts"
      spin={loading}
    />
  );
};

export default WorkflowModal;
